<script lang="ts">
  /**
   * A labelled text input: the label above, the input a rounded rectangle
   * (never a pill) with a hairline border and regular-weight text. The label
   * is a real `<label>` around the input, so a tap on the words focuses the
   * field and a screen reader names it. TwinTen's account form fields and its
   * "Play as" seat field.
   *
   * - `form`: a sentence-case label, on a form (sign in, change password).
   * - `seat`: a small uppercase label, where a name is asked because a seat
   *   is about to be taken.
   *
   * The field has no outer margin: the form around it spaces its fields
   * (TwinTen's 1rem between them is a `gap` on the form).
   *
   * `value` is bindable. `onenter` is Enter in the field. `input` is bindable
   * too, for a parent that has to move focus here.
   *
   * Since 1.7.0, `name` and `required` reach the input: a form a browser
   * submits or fills by name, and a field it must not leave empty
   * (`required` also tells assistive technology). The label is the game's
   * words: if the form marks its required fields, say so there.
   *
   * Test id: `testid`, on the input. No default.
   */
  import type { FullAutoFill } from 'svelte/elements';

  interface Props {
    value?: string;
    readonly label: string;
    readonly variant?: 'form' | 'seat';
    readonly type?: 'text' | 'password' | 'email';
    readonly placeholder?: string | undefined;
    readonly autocomplete?: FullAutoFill | undefined;
    readonly autocapitalize?: 'none' | 'characters' | 'words' | undefined;
    readonly inputmode?: 'text' | 'email' | 'numeric' | undefined;
    readonly enterkeyhint?: 'go' | 'next' | 'done' | 'send' | undefined;
    readonly minlength?: number | undefined;
    readonly maxlength?: number | undefined;
    readonly readonly?: boolean;
    readonly disabled?: boolean;
    /** Marks the input as where focus lands when the `Sheet` or `FullPage` it is in opens (`[data-autofocus]`). */
    readonly autofocus?: boolean;
    readonly onenter?: (() => void) | undefined;
    /** The id of a line that describes the input (a counter, a hint), for `aria-describedby`. */
    readonly describedby?: string | undefined;
    /** Marks the value as refused (`aria-invalid`), and draws the border in the danger colour. */
    readonly invalid?: boolean;
    input?: HTMLInputElement | undefined;
    readonly testid?: string | undefined;
    // ── 1.7.0 ──
    /** The input's `name`, for the form around it. */
    readonly name?: string | undefined;
    /** The input's `required`: the form cannot be sent with it empty. */
    readonly required?: boolean;
  }
  let {
    value = $bindable(''),
    label,
    variant = 'form',
    type = 'text',
    placeholder,
    autocomplete,
    autocapitalize,
    inputmode,
    enterkeyhint,
    minlength,
    maxlength,
    readonly = false,
    disabled = false,
    autofocus = false,
    onenter,
    describedby,
    invalid = false,
    input = $bindable(undefined),
    testid,
    name,
    required = false,
  }: Props = $props();

  function onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || onenter === undefined) return;
    event.preventDefault();
    onenter();
  }
</script>

<label class="ui-field ui-field--{variant}">
  <span class="ui-field-label">{label}</span>
  <input
    bind:this={input}
    bind:value
    class="ui-field-input"
    {type}
    {placeholder}
    {autocomplete}
    {autocapitalize}
    {inputmode}
    {enterkeyhint}
    {minlength}
    {maxlength}
    {readonly}
    {disabled}
    {name}
    {required}
    aria-describedby={describedby}
    aria-invalid={invalid ? 'true' : undefined}
    class:ui-field-input--invalid={invalid}
    autocorrect="off"
    spellcheck="false"
    data-autofocus={autofocus ? '' : undefined}
    data-testid={testid}
    onkeydown={onKeydown}
  />
</label>

<style>
  .ui-field {
    display: flex;
    flex-direction: column;
    min-width: 0;
    font-family: var(--ui-font);
  }

  .ui-field-label {
    font: var(--ui-text-caption);
    font-weight: 700;
    color: var(--ui-ink-2);
    margin-bottom: 0.3rem;
  }

  .ui-field-input {
    width: 100%;
    min-width: 0;
    min-height: 2.75rem;
    box-sizing: border-box;
    font: var(--ui-text-body);
    padding: 0.7rem 0.8rem;
    border: 1px solid var(--ui-line);
    border-radius: var(--ui-radius-sm);
    background: var(--ui-card);
    color: var(--ui-ink);
    -webkit-user-select: text;
    user-select: text;
  }

  .ui-field-input::placeholder {
    color: var(--ui-ink-2);
  }

  .ui-field-input:focus-visible {
    outline: 2px solid var(--ui-accent);
    outline-offset: 1px;
  }

  .ui-field-input:disabled {
    opacity: 0.6;
  }

  .ui-field-input--invalid {
    border-color: var(--ui-danger);
  }

  .ui-field--seat .ui-field-label {
    font: var(--ui-text-eyebrow);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: 0.35rem;
  }

  .ui-field--seat .ui-field-input {
    padding: 0.75rem 0.9rem;
    border-color: var(--ui-muted);
    border-radius: var(--ui-radius-sm);
    background: var(--ui-ground);
  }
</style>
